import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { Search as SearchIcon, LocateFixed, MapPin, Store, LayoutGrid, List, Map as MapIcon, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import CategoryIcon from "../components/CategoryIcon";
import HGMap from "../components/HGMap";
import TopVendorSpotlight from "../components/TopVendorSpotlight";
import { CATEGORY_OPTIONS } from "../lib/categories";

const TABS = [
  { key: "all", name: "All", icon: null },
  ...CATEGORY_OPTIONS.slice(0, 13).map((c) => ({ key: c.value, name: c.name, icon: c.icon })),
];

// Miles-based radius selector (customer-facing). Converted to km for backend.
const RADIUS_OPTIONS_MI = [
  { mi: 5,   label: "5 mi" },
  { mi: 10,  label: "10 mi" },
  { mi: 25,  label: "25 mi" },
  { mi: 50,  label: "50 mi" },
  { mi: 500, label: "Any" }, // effectively unlimited
];
const MI_PER_KM = 0.621371;
const kmToMi = (km) => km * MI_PER_KM;
const miToKm = (mi) => mi / MI_PER_KM;

const RADIUS_OPTIONS = [5, 10, 25, 50];

function DistanceBadge({ km }) {
  if (km == null) return null;
  const mi = kmToMi(km);
  const label = mi < 0.1 ? `${Math.round(km * 1000)} m` : `${mi < 10 ? mi.toFixed(1) : Math.round(mi)} mi`;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-200 border border-cyan-400/30">
      <MapPin className="w-3 h-3" /> {label}
    </span>
  );
}

function ProductCard({ p, onAdd, onOpen }) {
  const img = p.image_paths?.[0];
  const heroUrl = img
    ? fileUrl(img)
    : "https://images.unsplash.com/photo-1771016834009-bda415d14c64";
  return (
    <div
      className="group relative rounded-2xl overflow-hidden bg-zinc-900 border border-white/5 hover:border-pink-500/40 transition-all"
      data-testid={`search-card-${p.product_id}`}
    >
      <button onClick={onOpen} className="block w-full aspect-[3/4] overflow-hidden">
        <img
          src={heroUrl}
          alt={p.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      </button>
      {p.distance_km != null && (
        <div className="absolute top-2 left-2">
          <DistanceBadge km={p.distance_km} />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">
          @{p.vendor_name}
        </p>
        <h3 className="font-bold leading-tight truncate">{p.name}</h3>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-lg font-black text-gradient-neon">${p.price?.toFixed(2)}</span>
          <button
            onClick={onAdd}
            className="h-8 px-3 rounded-full bg-pink-500 hover:bg-pink-400 text-xs font-bold text-white glow-pink"
            data-testid={`search-add-${p.product_id}`}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorCard({ v, onOpen }) {
  const profile = v.vendor_profile || {};
  const avatar = profile.avatar_url ? fileUrl(profile.avatar_url) : null;
  return (
    <button
      onClick={onOpen}
      className="text-left rounded-2xl bg-zinc-900 border border-white/5 hover:border-cyan-400/40 transition-all p-4 flex items-center gap-3"
      data-testid={`search-vendor-${v.user_id}`}
    >
      <div className="w-14 h-14 rounded-full overflow-hidden bg-cyan-400/15 grid place-items-center border border-cyan-400/30 shrink-0">
        {avatar ? (
          <img src={avatar} alt={profile.business_name} className="w-full h-full object-cover" />
        ) : (
          <Store className="w-6 h-6 text-cyan-200" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-black truncate">{profile.business_name || v.name}</p>
        <p className="text-xs text-zinc-400 truncate">
          {profile.vendor_type || "—"} · {profile.location || "Local"}
        </p>
        <div className="mt-1.5">
          <DistanceBadge km={v.distance_km} />
        </div>
      </div>
    </button>
  );
}

export default function Search() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  // Near Me state
  const [nearMode, setNearMode] = useState(false);
  const [coords, setCoords] = useState(null); // {lat, lng}
  const [radiusMi, setRadiusMi] = useState(10);   // customer-facing miles
  const radiusKm = useMemo(() => miToKm(radiusMi), [radiusMi]);
  const [resultType, setResultType] = useState("products"); // 'products' | 'vendors'
  const [view, setView] = useState("list"); // 'list' | 'map'
  const [locating, setLocating] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        });
        setNearMode(true);
        setLocating(false);
      },
      (err) => {
        toast.error(err.code === 1 ? "Permission denied. Enable location in your browser." : "Couldn't get your location");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  const toggleNear = () => {
    if (nearMode) {
      setNearMode(false);
      return;
    }
    if (coords) {
      setNearMode(true);
    } else {
      requestLocation();
    }
  };

  // Search fetch (regular OR near-me)
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        if (nearMode && coords) {
          if (resultType === "products") {
            const { data } = await api.get("/discover/nearby-products", {
              params: {
                lat: coords.lat,
                lng: coords.lng,
                radius_km: radiusKm,
                category,
                q: q || undefined,
                limit: 60,
              },
            });
            setItems(data);
            setVendors([]);
          } else {
            const { data } = await api.get("/discover/nearby-vendors", {
              params: { lat: coords.lat, lng: coords.lng, radius_km: radiusKm, limit: 60 },
            });
            // optional client-side filter for q (vendor name)
            const filtered = q
              ? data.filter((v) =>
                  (v.vendor_profile?.business_name || v.name || "")
                    .toLowerCase()
                    .includes(q.toLowerCase()),
                )
              : data;
            setVendors(filtered);
            setItems([]);
          }
        } else {
          const { data } = await api.get("/products", {
            params: { q: q || undefined, category, limit: 60 },
          });
          setItems(data);
          setVendors([]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, category, nearMode, coords, radiusKm, resultType]);

  const empty = useMemo(() => {
    if (loading) return false;
    return nearMode && resultType === "vendors" ? vendors.length === 0 : items.length === 0;
  }, [loading, items, vendors, nearMode, resultType]);

  const onAdd = async (p) => {
    try {
      const res = await addItem(p.product_id, 1);
      if (res?.needs_customization) {
        toast.message(`Customize ${p.name}`);
      } else {
        toast.success(`${p.name} added`);
      }
    } catch {
      toast.error("Couldn't add to bag");
    }
  };

  return (
    <div className="px-4 pt-4 pb-6 max-w-6xl mx-auto" data-testid="search-page">
      {!q && !nearMode && (
        <div className="mb-4">
          <TopVendorSpotlight limit={3} />
        </div>
      )}
      <div className="relative">
        <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          placeholder={nearMode ? "Search nearby…" : "Search gems, vendors, tags…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-11 h-12 bg-white/5 border-white/10 rounded-2xl text-white placeholder:text-zinc-500 focus-visible:ring-pink-500"
          data-testid="search-input"
        />
      </div>

      {/* Near Me bar */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={toggleNear}
          disabled={locating}
          className={`h-10 px-4 rounded-full text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 transition-colors border ${
            nearMode
              ? "bg-cyan-400 text-black border-cyan-300 glow-cyan"
              : "bg-white/5 text-cyan-200 border-cyan-400/30 hover:bg-cyan-400/10"
          }`}
          data-testid="search-near-me-toggle"
        >
          <LocateFixed className="w-4 h-4" />
          {locating ? "Locating…" : nearMode ? "Near Me · ON" : "Near Me"}
        </button>

        {nearMode && coords && (
          <>
            <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-white/5 border border-white/10">
              {RADIUS_OPTIONS_MI.map((r) => (
                <button
                  key={r.mi}
                  onClick={() => setRadiusMi(r.mi)}
                  className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${
                    radiusMi === r.mi ? "bg-cyan-400 text-black" : "text-zinc-300 hover:text-white"
                  }`}
                  data-testid={`search-radius-${r.mi}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-white/5 border border-white/10">
              {[
                { k: "products", label: "Products" },
                { k: "vendors", label: "Vendors" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setResultType(t.k)}
                  className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${
                    resultType === t.k ? "bg-pink-500 text-white" : "text-zinc-300 hover:text-white"
                  }`}
                  data-testid={`search-result-type-${t.k}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {resultType === "vendors" && (
              <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-white/5 border border-white/10" data-testid="search-view-toggle">
                {[
                  { k: "list", label: "List", Icon: List },
                  { k: "map", label: "Map", Icon: MapIcon },
                ].map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setView(t.k)}
                    className={`h-8 px-3 rounded-full text-xs font-bold transition-colors inline-flex items-center gap-1 ${
                      view === t.k ? "bg-pink-500 text-white" : "text-zinc-300 hover:text-white"
                    }`}
                    data-testid={`search-view-${t.k}`}
                  >
                    <t.Icon className="w-3 h-3" /> {t.label}
                  </button>
                ))}
              </div>
            )}

            <p className="text-[11px] text-zinc-400 ml-auto inline-flex items-center gap-1" data-testid="search-near-me-coords">
              <MapPin className="w-3 h-3" />
              {coords.lat.toFixed(3)}, {coords.lng.toFixed(3)}
            </p>
          </>
        )}
      </div>

      {(!nearMode || resultType === "products") && (
        <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setCategory(t.key)}
              className={`px-4 h-10 rounded-full text-sm font-semibold whitespace-nowrap transition-all inline-flex items-center gap-1.5 ${
                category === t.key
                  ? "bg-pink-500 text-white glow-pink"
                  : "bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10"
              }`}
              data-testid={`search-tab-${t.key}`}
            >
              {t.icon ? (
                <CategoryIcon slug={t.icon} size={14} strokeWidth={2.2} />
              ) : (
                <LayoutGrid className="w-3.5 h-3.5" />
              )}
              {t.name}
            </button>
          ))}
        </div>
      )}

      {/* Vendor list */}
      {nearMode && resultType === "vendors" && view === "list" && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="search-vendors-grid">
          {vendors.map((v) => (
            <VendorCard key={v.user_id} v={v} onOpen={() => navigate(`/vendor/${v.user_id}`)} />
          ))}
        </div>
      )}

      {/* Vendor map */}
      {nearMode && resultType === "vendors" && view === "map" && (
        <div className="mt-5 relative rounded-2xl overflow-hidden" style={{ height: "60vh", minHeight: 420 }} data-testid="search-map">
          <HGMap
            vendors={vendors}
            center={coords}
            myLocation={coords}
            selectedId={selectedVendor?.user_id}
            onSelectVendor={(v) => setSelectedVendor(v)}
            onSearchArea={({ lat, lng, radius_km }) => {
              setCoords({ lat, lng });
              // Clamp new radius into the mile ladder
              const mi = Math.round(kmToMi(radius_km));
              const nearest = RADIUS_OPTIONS_MI.reduce((a, b) => Math.abs(b.mi - mi) < Math.abs(a.mi - mi) ? b : a);
              setRadiusMi(nearest.mi);
            }}
          />
          {selectedVendor && (
            <div className="absolute left-3 right-3 bottom-3 rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur p-3 flex items-center gap-3 z-[4]" data-testid="map-preview-card">
              {selectedVendor.vendor_profile?.avatar_url ? (
                <img
                  src={selectedVendor.vendor_profile.avatar_url.startsWith("http") ? selectedVendor.vendor_profile.avatar_url : fileUrl(selectedVendor.vendor_profile.avatar_url)}
                  alt=""
                  className="w-14 h-14 rounded-xl object-cover border border-white/10"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 grid place-items-center">
                  <Store className="w-5 h-5 text-zinc-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black tracking-tight truncate">{selectedVendor.vendor_profile?.business_name || selectedVendor.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <DistanceBadge km={selectedVendor.distance_km} />
                  {selectedVendor.reputation?.tier_name && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-pink-200">{selectedVendor.reputation.tier_name}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate(`/vendor/${selectedVendor.user_id}`)}
                className="h-9 px-4 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-xs font-black"
                data-testid="map-preview-view-shop"
              >
                View Shop
              </button>
              <button
                onClick={() => setSelectedVendor(null)}
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 grid place-items-center"
                aria-label="Close preview"
                data-testid="map-preview-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Product list */}
      {(!nearMode || resultType === "products") && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="search-products-grid">
          {items.map((p) => (
            <ProductCard
              key={p.product_id}
              p={p}
              onAdd={() => onAdd(p)}
              onOpen={() => navigate(`/vendor/${p.vendor_id}`)}
            />
          ))}
        </div>
      )}

      {empty && (
        <div className="text-center text-zinc-500 py-20" data-testid="search-empty">
          {nearMode
            ? `No Hidden Gems found within ${radiusMi === 500 ? "any distance" : `${radiusMi} mi`}. Try a larger radius or move the map.`
            : "Nothing here yet. Try another search."}
        </div>
      )}
    </div>
  );
}
